import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../services/accountingApi';
import Pagination from '../../../components/shared/Pagination';

function AccountGroupsIndex() {
    const [groups, setGroups] = useState([]);
    const [accountHeads, setAccountHeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterHead, setFilterHead] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });

    useEffect(() => {
        loadAccountHeads();
    }, []);

    useEffect(() => {
        loadGroups();
    }, [filterHead, filterType, filterStatus, currentPage]);

    const loadAccountHeads = async () => {
        try {
            const response = await accountingApi.getAccountHeads();
            if (response.success) {
                const heads = Array.isArray(response.data) ? response.data : (response.data?.account_heads || []);
                setAccountHeads(heads);
            }
        } catch (error) {
            console.error('Error loading account heads:', error);
        }
    };

    const loadGroups = async () => {
        try {
            const params: any = { page: currentPage };
            if (filterHead) params.account_head_id = filterHead;
            if (filterType) params.account_type = filterType;
            if (filterStatus) params.is_active = filterStatus === 'active';

            const response = await accountingApi.getAccountGroups(params);
            if (response.success) {
                const groupsData = Array.isArray(response.data) ? response.data : (response.data?.account_groups || response.data?.data || []);
                setGroups(groupsData);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (error) {
            console.error('Error loading account groups:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getTypeBadgeColor = (type: string) => {
        const colors: { [key: string]: string } = {
            'Assets': 'bg-blue-100 text-blue-700',
            'Liabilities': 'bg-red-100 text-red-700',
            'Income': 'bg-green-100 text-green-700',
            'Expense': 'bg-yellow-100 text-yellow-700',
            'Equity': 'bg-purple-100 text-purple-700'
        };
        return colors[type] || 'bg-slate-100 text-slate-700';
    };

    if (loading && groups.length === 0) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <Link to="/accounting" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </Link>
                        <h1 className="text-3xl font-bold text-slate-800">Account Groups</h1>
                    </div>
                    <p className="text-slate-600">Manage your financial account groups and categories ({pagination.total_count} found)</p>
                </div>
                <Link
                    to="/accounting/account-groups/new"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New Account Group
                </Link>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Account Head</label>
                        <select
                            value={filterHead}
                            onChange={(e) => { setFilterHead(e.target.value); setCurrentPage(1); }}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        >
                            <option value="">All Account Heads</option>
                            {accountHeads.map((head: any) => (
                                <option key={head.id} value={head.id}>{head.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Account Type</label>
                        <select
                            value={filterType}
                            onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        >
                            <option value="">All Types</option>
                            <option value="Assets">Assets</option>
                            <option value="Liabilities">Liabilities</option>
                            <option value="Income">Income</option>
                            <option value="Expense">Expense</option>
                            <option value="Equity">Equity</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Account Groups Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-bold text-slate-800">Account Groups</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Account Head</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Balance</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {groups.length > 0 ? groups.map((group: any) => (
                                <tr key={group.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-slate-900">{group.name}</div>
                                        {group.description && (
                                            <div className="text-sm text-slate-500 mt-0.5">{group.description}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-700">
                                        {group.account_head?.name || group.account_head_name || '-'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeBadgeColor(group.account_head?.account_type || group.account_type)}`}>
                                            {group.account_head?.account_type || group.account_type || '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right font-medium text-slate-900">
                                        ₹{(group.opening_balance || group.current_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`text-xs px-2 py-1 rounded-full ${group.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                            {group.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <Link
                                            to={`/accounting/account-groups/${group.id}`}
                                            className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                                        >
                                            View
                                        </Link>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                                        No account groups found. Create one to get started.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalCount={pagination.total_count}
                perPage={pagination.per_page}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default AccountGroupsIndex;
